/**
 * Sync Service - Offline-first synchronization with Azure Table Storage
 *
 * Key features:
 * - 304 Not-Modified checks using blob-stored manifests
 * - Push local changes first, then pull server changes
 * - Conflict detection with last-write-wins resolution
 * - Background sync scheduling
 */

import { dbManager, getDb, type SyncStatus } from "../../db/user-database";
import type {
  SyncManifest,
  SyncCheckResult,
  PushOperation,
  PushResult,
  PullResult,
  SyncResult,
  SyncState,
  MonitoredUserDataResponse,
} from "./types";
import { INITIAL_SYNC_STATE } from "./types";
import { apiBase } from "../api-config";
import { settingsSyncService } from "../settings-sync";

// API base URL (dynamic for dev vs prod)
const API_BASE = apiBase;

// Sync intervals
const PERIODIC_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const DEBOUNCE_DELAY = 2000; // 2 seconds after local write

// Table names for sync
const SYNC_TABLES = [
  "entries",
  "periods",
  "settings",
  "cgmReadings",
  "dayNotes",
] as const;
type SyncTable = (typeof SYNC_TABLES)[number];

// Date fields that need to be converted from ISO strings to Date objects
// when pulling data from the server (JSON serialization loses Date type)
const DATE_FIELDS_BY_TABLE: Record<SyncTable, string[]> = {
  entries: ["timestamp", "createdAt", "updatedAt"],
  periods: [],
  settings: [],
  cgmReadings: ["timestamp"],
  dayNotes: [],
};

/**
 * Sanitize data from server by converting ISO date strings to Date objects.
 * JSON.parse() returns strings for dates, but we need Date objects for
 * IndexedDB queries to work correctly.
 */
function sanitizeDatesFromServer(
  table: SyncTable,
  data: Record<string, unknown>,
): Record<string, unknown> {
  const dateFields = DATE_FIELDS_BY_TABLE[table];
  if (dateFields.length === 0) return data;

  const sanitized = { ...data };
  for (const field of dateFields) {
    const value = sanitized[field];
    if (value && typeof value === "string") {
      sanitized[field] = new Date(value);
    } else if (value && typeof value === "number") {
      sanitized[field] = new Date(value);
    }
  }
  return sanitized;
}

// Sync state listener
type SyncStateListener = (state: SyncState) => void;

class SyncService {
  private _state: SyncState = INITIAL_SYNC_STATE;
  private _listeners: Set<SyncStateListener> = new Set();
  private _syncInProgress = false;
  private _syncTimer: number | null = null;
  private _periodicTimer: number | null = null;
  private _cachedEtag: string | null = null;
  private _isOnline = navigator.onLine;

  /**
   * Current sync state
   */
  get state(): SyncState {
    return this._state;
  }

  /**
   * Subscribe to sync state changes
   */
  subscribe(listener: SyncStateListener): () => void {
    this._listeners.add(listener);
    listener(this._state);
    return () => {
      this._listeners.delete(listener);
    };
  }

  /**
   * Update state and notify listeners
   */
  private setState(partial: Partial<SyncState>): void {
    this._state = { ...this._state, ...partial };
    this._listeners.forEach((listener) => listener(this._state));
  }

  /**
   * Initialize sync service
   */
  async initialize(): Promise<void> {
    // Set up online/offline listeners
    window.addEventListener("online", () => this.onOnline());
    window.addEventListener("offline", () => this.onOffline());
    this._isOnline = navigator.onLine;

    // Check if we've completed initial sync before (persisted in DB)
    // This tells us if we have LOCAL data, but we still need to sync this session
    // to get any updates from other devices
    let hasPreviousSyncData = false;
    if (dbManager.isInitialized) {
      const syncMeta = await getDb().syncMetadata.get("state");
      if (
        syncMeta?.tableVersions &&
        Object.keys(syncMeta.tableVersions).length > 0
      ) {
        hasPreviousSyncData = true;
        console.log("[Sync] Previous sync detected from metadata");
      }
    }

    if (!this._isOnline) {
      // Offline: if we have previous sync data, mark complete (use local data)
      // Otherwise stay incomplete (fresh install with no data)
      this.setState({
        status: "offline",
        initialSyncComplete: hasPreviousSyncData,
      });
      return;
    }

    // Initial sync - this will set initialSyncComplete when done
    await this.checkAndSync();

    // Start periodic checks
    this.startPeriodicCheck();
  }

  /**
   * Start periodic sync checks
   */
  private startPeriodicCheck(): void {
    if (this._periodicTimer) {
      clearInterval(this._periodicTimer);
    }

    this._periodicTimer = window.setInterval(() => {
      if (this._isOnline && !this._syncInProgress) {
        this.checkAndSync();
      }
    }, PERIODIC_CHECK_INTERVAL);
  }

  /**
   * Stop periodic checks
   */
  private stopPeriodicCheck(): void {
    if (this._periodicTimer) {
      clearInterval(this._periodicTimer);
      this._periodicTimer = null;
    }
  }

  /**
   * Handle coming online
   */
  private async onOnline(): Promise<void> {
    this._isOnline = true;
    this.setState({ status: "idle" });

    // Immediate sync when coming online
    await this.checkAndSync();
    this.startPeriodicCheck();
  }

  /**
   * Handle going offline
   */
  private onOffline(): void {
    this._isOnline = false;
    this.setState({ status: "offline" });
    this.stopPeriodicCheck();
  }

  /**
   * Quick check for updates using 304 Not-Modified
   */
  async checkForUpdates(): Promise<SyncCheckResult> {
    if (!this._isOnline || !dbManager.isInitialized) {
      return { hasChanges: false, offline: !this._isOnline };
    }

    try {
      this.setState({ status: "checking" });

      const headers: HeadersInit = {
        Accept: "application/json",
      };

      // Get sync metadata to check if we have any local data
      const syncMeta = await getDb().syncMetadata.get("state");

      // Only use ETag/If-Modified-Since if we have local data
      // If tableVersions are all 0 or missing, we need a full sync
      const hasLocalData =
        syncMeta?.tableVersions &&
        Object.values(syncMeta.tableVersions).some((v) => v > 0);

      if (hasLocalData) {
        // Add ETag for 304 check
        if (this._cachedEtag) {
          headers["If-None-Match"] = this._cachedEtag;
        }

        // Add last modified from sync metadata
        if (syncMeta?.lastSyncTimestamp) {
          headers["If-Modified-Since"] = syncMeta.lastSyncTimestamp;
        }
      } else {
        // No local data - clear cached ETag to force full sync
        this._cachedEtag = null;
        console.log("No local data found - forcing full manifest fetch");
      }

      const response = await fetch(`${API_BASE}/api/sync/manifest`, {
        method: "GET",
        credentials: "include",
        headers,
      });

      // 304 - No changes (server data matches our cached version)
      // This means we're already synced, so mark initial sync as complete
      if (response.status === 304) {
        this.setState({ status: "idle", initialSyncComplete: true });
        return { hasChanges: false };
      }

      // 401 - Not authenticated
      if (response.status === 401) {
        this.setState({ status: "idle" });
        return { hasChanges: false };
      }

      // Error response
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      // Parse manifest
      const manifest: SyncManifest = await response.json();
      const etag = response.headers.get("ETag") || undefined;

      if (etag) {
        this._cachedEtag = etag;
      }

      return {
        hasChanges: true,
        manifest,
        etag,
      };
    } catch (error) {
      console.error("Sync check failed:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Sync check failed";
      this.setState({
        status: "error",
        error: errorMessage,
      });
      return { hasChanges: false, error: errorMessage };
    }
  }

  /**
   * Push local changes to server
   * Batches operations for better performance with large datasets
   * NOTE: Settings are excluded - they have their own dedicated sync via SettingsController
   */
  async pushChanges(): Promise<PushResult> {
    const db = getDb();
    const operations: PushOperation[] = [];

    // Collect all pending changes (excluding settings - handled separately)
    const pushTables = SYNC_TABLES.filter((t) => t !== "settings");
    for (const table of pushTables) {
      const pending = await (db[table] as any)
        .where("_syncStatus")
        .equals("pending")
        .toArray();

      for (const item of pending) {
        const isDeleted = !!item._deletedAt;
        operations.push({
          type: isDeleted
            ? "delete"
            : item._serverVersion
              ? "update"
              : "create",
          table,
          id: item.id,
          data: item,
          localVersion: item._localVersion || Date.now(),
          serverVersion: item._serverVersion,
        });
      }
    }

    if (operations.length === 0) {
      return { succeeded: [], conflicts: [], errors: [] };
    }

    // Batch size - balance between HTTP overhead and request size
    const BATCH_SIZE = 500;
    const allSucceeded: PushResult["succeeded"] = [];
    const allConflicts: PushResult["conflicts"] = [];
    const allErrors: PushResult["errors"] = [];

    // Process in batches
    for (let i = 0; i < operations.length; i += BATCH_SIZE) {
      const batch = operations.slice(i, i + BATCH_SIZE);
      const batchNum = Math.floor(i / BATCH_SIZE) + 1;
      const totalBatches = Math.ceil(operations.length / BATCH_SIZE);

      console.log(
        `Syncing batch ${batchNum}/${totalBatches} (${batch.length} items)`,
      );

      try {
        const response = await fetch(`${API_BASE}/api/sync/push`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ operations: batch }),
        });

        if (!response.ok) {
          throw new Error(`Push failed: ${response.status}`);
        }

        const result: PushResult = await response.json();

        allSucceeded.push(...result.succeeded);
        allConflicts.push(...result.conflicts);
        allErrors.push(...result.errors);

        // Update local records with server versions for this batch
        for (const success of result.succeeded) {
          const table = db[success.table as SyncTable] as any;
          await table.update(success.id, {
            _syncStatus: "synced" as SyncStatus,
            _serverVersion: success.serverVersion,
            _localVersion: undefined,
          });
        }

        // Handle conflicts for this batch
        // CRITICAL: Keep LOCAL data (user's changes), store SERVER data as reference
        // This ensures user changes are not lost during conflict resolution
        for (const conflict of result.conflicts) {
          const table = db[conflict.table as SyncTable] as any;
          console.warn(
            `[Sync] Conflict detected for ${conflict.table}/${conflict.id}. ` +
              `Local version will be kept, server data stored for reference.`,
          );
          // Keep local data intact, just update sync metadata
          // Store SERVER data in _conflictData so user can compare/resolve
          await table.update(conflict.id, {
            _syncStatus: "conflict" as SyncStatus,
            _serverVersion: conflict.serverVersion,
            _conflictData: conflict.serverData, // Store SERVER data, keep local fields
            _conflictAt: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error(`Batch ${batchNum} failed:`, error);
        // Mark remaining items in this batch as errors
        for (const op of batch) {
          allErrors.push({
            table: op.table,
            id: op.id,
            error: error instanceof Error ? error.message : "Batch failed",
          });
        }
        // Continue with next batch instead of failing entirely
      }
    }

    // Update pending count
    await this.updatePendingCount();

    console.log(
      `Sync complete: ${allSucceeded.length} succeeded, ${allConflicts.length} conflicts, ${allErrors.length} errors`,
    );

    return {
      succeeded: allSucceeded,
      conflicts: allConflicts,
      errors: allErrors,
    };
  }

  /**
   * Pull changes from server
   * NOTE: Settings are excluded - they have their own dedicated sync via SettingsController/SSE
   */
  async pullChanges(manifest: SyncManifest): Promise<void> {
    const db = getDb();
    let syncMeta = await db.syncMetadata.get("state");

    // Check which tables have changes by comparing per-table versions
    // Only pull tables where server version > local version
    // NOTE: Settings excluded - handled by dedicated settings-sync service
    const tablesToPull: SyncTable[] = [];
    const pullTables = SYNC_TABLES.filter((t) => t !== "settings");

    for (const table of pullTables) {
      const serverVersion =
        manifest.tableVersions?.[
          table as keyof typeof manifest.tableVersions
        ] ?? 0;
      const localVersion = syncMeta?.tableVersions?.[table] ?? 0;

      // If server version is higher, we need to pull
      if (serverVersion > localVersion) {
        tablesToPull.push(table);
      } else if (localVersion > serverVersion && serverVersion > 0) {
        // Fix: If local version is higher than server version, something is wrong
        // This can happen due to a previous bug where global version was stored instead of table version
        // Reset local version to force a fresh pull
        console.warn(
          `[Sync] Detected version mismatch for ${table}: local=${localVersion}, server=${serverVersion}. Forcing resync.`,
        );
        tablesToPull.push(table);
        // Reset the local version for this table to 0 to force full pull
        if (syncMeta?.tableVersions) {
          syncMeta.tableVersions[table] = 0;
          await db.syncMetadata.put(syncMeta);
          // Refresh syncMeta after update
          syncMeta = await db.syncMetadata.get("state");
        }
      }
    }
    if (tablesToPull.length === 0) {
      return;
    }

    // Pull each changed table
    for (const table of tablesToPull) {
      try {
        const sinceVersion = syncMeta?.tableVersions?.[table] || 0;

        // For CGM readings, limit to last 90 days to avoid pulling entire history
        const pullBody: { sinceVersion: number; sinceDate?: string } = {
          sinceVersion,
        };
        if (table === "cgmReadings") {
          const ninetyDaysAgo = new Date();
          ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
          pullBody.sinceDate = ninetyDaysAgo.toISOString();
        }

        const response = await fetch(`${API_BASE}/api/sync/pull/${table}`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(pullBody),
        });

        if (!response.ok) {
          console.error(`Pull failed for ${table}: ${response.status}`);
          continue;
        }

        const result: PullResult = await response.json();

        // Apply changes
        const tableRef = db[table] as any;

        for (const change of result.changes) {
          if (change.isDeleted) {
            // Remove deleted items (unless we have local changes)
            const local = await tableRef.get(change.id);
            if (!local || local._syncStatus !== "pending") {
              await tableRef.delete(change.id);
            }
          } else {
            // Upsert with server data
            const local = await tableRef.get(change.id);

            // Skip if we have pending local changes
            if (local && local._syncStatus === "pending") {
              continue;
            }

            // CRITICAL: Sanitize dates from server
            // JSON serialization converts Date objects to ISO strings,
            // but we need Date objects for IndexedDB queries to work
            const changeData = change.data as Record<string, unknown>;
            const sanitizedData = sanitizeDatesFromServer(table, changeData);

            await tableRef.put({
              ...sanitizedData,
              id: change.id,
              _syncStatus: "synced" as SyncStatus,
              _serverVersion: change.version,
            });
          }
        }

        // Update table version
        const currentMeta = (await db.syncMetadata.get("state")) || {
          key: "state" as const,
          localVersion: "",
          tableVersions: {},
        };

        await db.syncMetadata.put({
          ...currentMeta,
          tableVersions: {
            ...currentMeta.tableVersions,
            [table]: result.currentVersion,
          },
        });
      } catch (error) {
        console.error(`Error pulling ${table}:`, error);
      }
    }

    // Update sync metadata with server versions
    const finalMeta = await db.syncMetadata.get("state");
    await db.syncMetadata.put({
      key: "state",
      lastSyncTimestamp: new Date().toISOString(),
      localVersion: manifest.etag || String(manifest.version),
      serverChecksum: JSON.stringify(manifest.tableVersions),
      tableVersions: finalMeta?.tableVersions || {},
    });
  }

  /**
   * Sync monthly archive blobs.
   * Downloads archived months that haven't been fetched yet and applies
   * records to the local Dexie tables. Archives are immutable once downloaded.
   */
  private async syncArchives(manifest: SyncManifest): Promise<void> {
    if (!manifest.archives?.tables) return;

    const db = getDb();
    const archiveMeta = await db.archiveMetadata.get("state");
    const downloaded: Record<string, string[]> = archiveMeta?.downloadedArchives ?? {};

    let totalApplied = 0;

    for (const [table, months] of Object.entries(manifest.archives.tables)) {
      const localMonths = new Set(downloaded[table] ?? []);

      for (const archiveMonth of months) {
        if (localMonths.has(archiveMonth.month)) continue;

        try {
          // Request archive — server returns 302 redirect to SAS URL
          const response = await fetch(
            `${API_BASE}/api/sync/archive/${table}/${archiveMonth.month}`,
            { credentials: "include", redirect: "follow" },
          );

          if (!response.ok) {
            console.warn(`[Sync] Failed to fetch archive ${table}/${archiveMonth.month}: ${response.status}`);
            continue;
          }

          const archive = await response.json();

          // Apply records to local Dexie table
          const tableRef = db[table as SyncTable] as any;
          if (!tableRef) continue;

          for (const record of archive.records) {
            // Don't overwrite local pending changes
            const existing = await tableRef.get(record.id);
            if (existing && (existing as { _syncStatus?: string })._syncStatus === "pending") continue;

            if (record.isDeleted) {
              await tableRef.delete(record.id);
            } else {
              const data = sanitizeDatesFromServer(table as SyncTable, record.data);
              await tableRef.put({
                ...data,
                id: record.id,
                _syncStatus: "synced" as SyncStatus,
                _serverVersion: record.version,
              });
            }
          }

          // Mark month as downloaded
          if (!downloaded[table]) downloaded[table] = [];
          downloaded[table].push(archiveMonth.month);
          totalApplied += archive.records.length;

          console.log(`[Sync] Applied archive ${table}/${archiveMonth.month}: ${archive.records.length} records`);
        } catch (error) {
          console.warn(`[Sync] Error fetching archive ${table}/${archiveMonth.month}:`, error);
        }
      }
    }

    // Persist archive metadata
    await db.archiveMetadata.put({
      key: "state",
      downloadedArchives: downloaded,
      activeDataSince: manifest.archives.activeDataSince?.toString(),
    });

    if (totalApplied > 0) {
      console.log(`[Sync] Applied ${totalApplied} records from archives`);
    }
  }

  /**
   * Full sync operation: check → push → pull
   * CRITICAL: On first sync (initialSyncComplete=false), we PULL ONLY to avoid
   * overwriting server data with local defaults.
   * @param preCheckedManifest - Optional manifest from a prior checkForUpdates() to avoid redundant call
   */
  async sync(preCheckedManifest?: SyncManifest | null): Promise<SyncResult> {
    if (this._syncInProgress) {
      return { success: false, skipped: true };
    }

    if (!this._isOnline || !dbManager.isInitialized) {
      return { success: false, error: "Offline or not initialized" };
    }

    this._syncInProgress = true;
    this.setState({ status: "syncing" });

    try {
      // 1. Check for server changes (skip if manifest already provided)
      const checkResult =
        preCheckedManifest !== undefined
          ? {
              hasChanges: preCheckedManifest !== null,
              manifest: preCheckedManifest,
            }
          : await this.checkForUpdates();

      // Detect fresh user: if server has no data (all table versions = 0)
      // This is CRITICAL for sync protection - only a fresh user can write without syncing first
      let isFreshUser = false;
      if (checkResult.manifest) {
        const tableVersions = checkResult.manifest.tableVersions;
        isFreshUser = Object.values(tableVersions).every((v) => v === 0);
        if (isFreshUser && !this._state.isConfirmedFreshUser) {
          console.log(
            "[Sync] Server confirms this is a fresh user with no data",
          );
          this.setState({ isConfirmedFreshUser: true });
        }
      }

      // First sync protection: only applies to truly fresh devices
      // If there are pending local changes, we must push them (not skip)
      const pendingCount = await this.getPendingCount();
      const isFirstSync =
        !this._state.initialSyncComplete && pendingCount === 0;

      let pushedCount = 0;
      let conflictCount = 0;
      let pulledCount = 0;

      if (isFirstSync) {
        // First sync on a truly fresh device - PULL ONLY
        // DB starts empty, sync pulls server data, then creates defaults if truly new user
        console.log("[Sync] First sync on fresh device - pulling server data");

        if (checkResult.hasChanges && checkResult.manifest) {
          await this.pullChanges(checkResult.manifest);
          pulledCount = Object.values(checkResult.manifest.counts).reduce(
            (a, b) => a + b,
            0,
          );
        }

        // After first sync: if server had no data, create defaults for new user
        if (isFreshUser) {
          console.log("[Sync] New user confirmed - creating default settings");
          await dbManager.initializeDefaultsForNewUser();
        }
        console.log("[Sync] First sync complete");
      } else {
        // Normal sync: push then pull
        // 2. Push local changes first
        const pushResult = await this.pushChanges();
        pushedCount = pushResult.succeeded.length;
        conflictCount = pushResult.conflicts.length;

        // 3. Pull server changes if manifest indicates changes
        if (checkResult.hasChanges && checkResult.manifest) {
          await this.pullChanges(checkResult.manifest);
          pulledCount = Object.values(checkResult.manifest.counts).reduce(
            (a, b) => a + b,
            0,
          );

          // 4. Sync archived monthly blobs (if any)
          await this.syncArchives(checkResult.manifest);
        }
      }

      // Update state - mark initial sync complete
      await this.updatePendingCount();
      this.setState({
        status: "idle",
        lastSyncTime: new Date(),
        conflicts: conflictCount,
        error: undefined,
        initialSyncComplete: true,
      });

      return {
        success: true,
        pushed: pushedCount,
        pulled: pulledCount,
        conflicts: conflictCount,
      };
    } catch (error) {
      console.error("Sync failed:", error);
      this.setState({
        status: "error",
        error: error instanceof Error ? error.message : "Sync failed",
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Sync failed",
      };
    } finally {
      this._syncInProgress = false;
    }
  }

  /**
   * Convenience method: check and sync if needed
   */
  async checkAndSync(): Promise<SyncResult> {
    const checkResult = await this.checkForUpdates();

    // If check failed with error, return failure
    if (checkResult.error) {
      return { success: false, error: checkResult.error };
    }

    // Detect fresh user from manifest if available
    if (checkResult.manifest) {
      const tableVersions = checkResult.manifest.tableVersions;
      const isFreshUser = Object.values(tableVersions).every((v) => v === 0);
      if (isFreshUser && !this._state.isConfirmedFreshUser) {
        console.log("[Sync] Server confirms this is a fresh user with no data");
        this.setState({
          isConfirmedFreshUser: true,
          initialSyncComplete: true,
        });
      }
    }

    // Also sync if we have pending local changes
    const pendingCount = await this.getPendingCount();

    if (checkResult.hasChanges || pendingCount > 0) {
      // Pass the manifest from checkForUpdates to avoid redundant server call
      return this.sync(checkResult.manifest);
    }

    // No changes needed - but mark initial sync as complete
    // This is important: even if there's nothing to sync, we've confirmed
    // the server state and can safely allow writes
    if (!this._state.initialSyncComplete) {
      this.setState({ initialSyncComplete: true });
    }

    return { success: true, pushed: 0, pulled: 0 };
  }

  /**
   * Schedule sync soon (debounced) - call after local writes
   */
  scheduleSyncSoon(): void {
    if (this._syncTimer) {
      clearTimeout(this._syncTimer);
    }

    this._syncTimer = window.setTimeout(() => {
      this._syncTimer = null;
      if (this._isOnline && !this._syncInProgress) {
        this.sync();
      }
    }, DEBOUNCE_DELAY);
  }

  /**
   * Get count of pending changes
   */
  private async getPendingCount(): Promise<number> {
    if (!dbManager.isInitialized) return 0;

    const db = getDb();
    let total = 0;

    for (const table of SYNC_TABLES) {
      const count = await (db[table] as any)
        .where("_syncStatus")
        .equals("pending")
        .count();
      total += count;
    }

    return total;
  }

  /**
   * Update pending count in state
   */
  private async updatePendingCount(): Promise<void> {
    const pendingCount = await this.getPendingCount();

    // Also count conflicts
    let conflictCount = 0;
    if (dbManager.isInitialized) {
      const db = getDb();
      for (const table of SYNC_TABLES) {
        const count = await (db[table] as any)
          .where("_syncStatus")
          .equals("conflict")
          .count();
        conflictCount += count;
      }
    }

    this.setState({
      pendingChanges: pendingCount,
      conflicts: conflictCount,
    });
  }

  /**
   * Check if it's safe to write data to the local database.
   *
   * CRITICAL: This protects against accidental data loss when logging into a new device.
   * A new device has an empty database. If we allow writes before syncing, default/empty
   * settings could overwrite the user's actual data on the server.
   *
   * Writes are allowed if ANY of these are true:
   * 1. Initial sync has completed (we have server data)
   * 2. This is a confirmed fresh user (server has no data - verified via manifest)
   * 3. We have existing local data (not a fresh device)
   * 4. We're offline (offline-first - must allow offline writes)
   *
   * @returns Object with `allowed` boolean and `reason` string
   */
  async canWriteData(): Promise<{ allowed: boolean; reason: string }> {
    // Always allow if initial sync completed
    if (this._state.initialSyncComplete) {
      return { allowed: true, reason: "Initial sync completed" };
    }

    // Allow if server confirmed this is a fresh user with no data
    if (this._state.isConfirmedFreshUser) {
      return { allowed: true, reason: "Confirmed fresh user" };
    }

    // Allow if offline (offline-first principle)
    if (!this._isOnline) {
      return { allowed: true, reason: "Offline mode" };
    }

    // Check if we have existing local data (existing device, not fresh install)
    if (dbManager.isInitialized) {
      const db = getDb();
      const syncMeta = await db.syncMetadata.get("state");

      // If we have tableVersions with any value > 0, we've synced before
      if (syncMeta?.tableVersions) {
        const hasLocalData = Object.values(syncMeta.tableVersions).some(
          (v) => v > 0,
        );
        if (hasLocalData) {
          return { allowed: true, reason: "Existing local data found" };
        }
      }

      // Also check if we have any settings record (even pending)
      const settingsCount = await db.settings.count();
      if (settingsCount > 0) {
        return { allowed: true, reason: "Existing settings found" };
      }
    }

    // Not safe to write - initial sync hasn't completed on fresh device
    return {
      allowed: false,
      reason:
        "Waiting for initial sync to complete. This prevents data loss on new devices.",
    };
  }

  /**
   * Cleanup on logout
   */
  cleanup(): void {
    this.stopPeriodicCheck();
    if (this._syncTimer) {
      clearTimeout(this._syncTimer);
      this._syncTimer = null;
    }
    this._cachedEtag = null;
    this.setState(INITIAL_SYNC_STATE);
  }

  // ==========================================
  // Monitored User Data Access (Read-Only)
  // ==========================================

  /**
   * Get manifest for a monitored user (for monitors/clinicians)
   * Returns data directly - does not sync to local DB
   */
  async getMonitoredManifest(
    diabeticUserId: string,
  ): Promise<SyncManifest | null> {
    try {
      const response = await fetch(
        `${API_BASE}/api/sync/monitored/${diabeticUserId}/manifest`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (response.status === 403 || response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Failed to get monitored manifest:", error);
      return null;
    }
  }

  /**
   * Pull data for a specific table from a monitored user
   * Returns data directly - does not sync to local DB
   */
  async pullMonitoredData(
    diabeticUserId: string,
    table: SyncTable,
    sinceVersion = 0,
  ): Promise<PullResult | null> {
    try {
      // For CGM readings, limit to last 90 days
      const pullBody: { sinceVersion: number; sinceDate?: string } = {
        sinceVersion,
      };
      if (table === "cgmReadings") {
        const ninetyDaysAgo = new Date();
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        pullBody.sinceDate = ninetyDaysAgo.toISOString();
      }

      const response = await fetch(
        `${API_BASE}/api/sync/monitored/${diabeticUserId}/pull/${table}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(pullBody),
        },
      );

      if (response.status === 403 || response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Failed to pull monitored data for ${table}:`, error);
      return null;
    }
  }

  /**
   * Fetch data for a monitored user (for viewing dashboard) with lazy loading.
   * Uses the single /data endpoint which returns all tables in one request.
   * Use the 'days' parameter to limit data for better performance (default: 3).
   */
  async fetchMonitoredUserData(
    diabeticUserId: string,
    days: number = 3,
  ): Promise<{
    entries: unknown[];
    periods: unknown[];
    settings: unknown[];
    cgmReadings: unknown[];
    dayNotes: unknown[];
  } | null> {
    try {
      const response = await fetch(
        `${API_BASE}/api/sync/monitored/${diabeticUserId}/data?days=${days}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (response.status === 403 || response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data: MonitoredUserDataResponse = await response.json();

      return {
        entries: data.entries,
        periods: data.periods,
        settings: data.settings,
        cgmReadings: data.cgmReadings,
        dayNotes: data.dayNotes || [],
      };
    } catch (error) {
      console.error("Failed to fetch monitored user data:", error);
      return null;
    }
  }

  /**
   * Add a clinic note to a monitored user's data.
   * This allows clinicians to add notes that will appear in the patient's history view.
   */
  async addMonitoredUserNote(
    diabeticUserId: string,
    date: string,
    content: string,
  ): Promise<{ noteId: string; createdAt: string } | null> {
    try {
      const response = await fetch(
        `${API_BASE}/api/sync/monitored/${diabeticUserId}/note`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ date, content }),
        },
      );

      if (response.status === 403 || response.status === 404) {
        console.error("Not authorized to add notes to this user");
        return null;
      }

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();
      return {
        noteId: result.noteId,
        createdAt: result.createdAt,
      };
    } catch (error) {
      console.error("Failed to add note to monitored user:", error);
      return null;
    }
  }

  /**
   * Delete all user data from both local storage and server.
   * This is a destructive operation that cannot be undone.
   */
  async deleteAllData(): Promise<{ success: boolean; error?: string }> {
    try {
      this.setState({ status: "syncing" });

      // First, delete from server if online
      if (this._isOnline) {
        try {
          const response = await fetch(`${API_BASE}/api/sync/data`, {
            method: "DELETE",
            credentials: "include",
          });

          if (!response.ok && response.status !== 404) {
            // 404 is ok - means no data to delete
            console.warn("Server deletion warning:", response.status);
          }
        } catch (serverError) {
          // Log but continue with local deletion
          console.warn(
            "Server deletion failed, continuing with local:",
            serverError,
          );
        }
      }

      // Delete all local databases
      const db = await getDb();
      if (db) {
        // Clear all tables
        await db.entries.clear();
        await db.periods.clear();
        await db.settings.clear();
        await db.cgmReadings.clear();
        await db.syncMetadata.clear();
      }

      // Reset cached etag
      this._cachedEtag = null;

      this.setState({
        status: this._isOnline ? "idle" : "offline",
        lastSyncTime: undefined,
        pendingChanges: 0,
      });

      return { success: true };
    } catch (error) {
      console.error("Failed to delete all data:", error);
      this.setState({ status: "error" });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Force pull all data from server, replacing local data
   * Use this to reset local state to match server
   */
  async forcePullAll(): Promise<SyncResult> {
    if (this._syncInProgress) {
      return { success: false, error: "Sync already in progress" };
    }

    if (!this._isOnline) {
      return { success: false, error: "Offline - cannot pull from server" };
    }

    this._syncInProgress = true;
    this.setState({
      status: "syncing",
      initialSyncComplete: false, // Reset until pull completes
    });

    try {
      const db = getDb();

      // Clear all local sync tables (except settings - handled separately)
      console.log("[ForcePull] Clearing local data...");
      const clearTables = SYNC_TABLES.filter((t) => t !== "settings");
      for (const table of clearTables) {
        await (db[table] as any).clear();
      }

      // Reset sync metadata to force full pull
      await db.syncMetadata.put({
        key: "state",
        localVersion: "",
        tableVersions: {},
      });

      console.log("[ForcePull] Pulling all data from server...");

      // Get fresh manifest
      const checkResult = await this.checkForUpdates();
      if (!checkResult.manifest) {
        throw new Error("Failed to get server manifest");
      }

      // Pull all data
      await this.pullChanges(checkResult.manifest);

      // Force refresh settings via dedicated service
      await settingsSyncService.checkAndSync();

      // Count what we pulled (excluding settings)
      let pulledCount = 0;
      for (const table of SYNC_TABLES) {
        const count = await (db[table] as any).count();
        pulledCount += count;
      }

      console.log(`[ForcePull] Complete. Pulled ${pulledCount} items.`);

      this.setState({
        status: "idle",
        lastSyncTime: new Date(),
        error: undefined,
        initialSyncComplete: true,
        isConfirmedFreshUser: pulledCount === 0,
      });

      await this.updatePendingCount();

      return {
        success: true,
        pulled: pulledCount,
      };
    } catch (error) {
      console.error("Force pull failed:", error);
      this.setState({
        status: "error",
        error: error instanceof Error ? error.message : "Force pull failed",
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Force pull failed",
      };
    } finally {
      this._syncInProgress = false;
    }
  }
}

// Singleton instance
export const syncService = new SyncService();

// Re-export types
export * from "./types";
